import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Reveal — pure-CSS scroll reveal wrapper. Zero JS deps, runs on
 * IntersectionObserver. Designed to drop framer-motion from the home-route
 * bundle (saves ~37 KB gz).
 *
 * Motion spec (UI_SYSTEM §5.3):
 *   initial:  opacity 0, translateY 12px, blur 6px
 *   visible:  opacity 1, translateY 0,   blur 0
 *   duration: 600ms  ease: cubic-bezier(0.4,0,0.2,1)
 *   stagger:  children 60ms apart when inside <RevealGroup>
 *
 * Reduced-motion: instant paint, skips the transition entirely.
 */

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

function useInViewOnce<T extends Element>(rootMargin = "-60px") {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.05, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return { ref, visible };
}

const BASE_STYLE: React.CSSProperties = {
  transition: "opacity 600ms cubic-bezier(0.4,0,0.2,1), transform 600ms cubic-bezier(0.4,0,0.2,1), filter 600ms cubic-bezier(0.4,0,0.2,1)",
  willChange: "opacity, transform, filter",
};
const HIDDEN: React.CSSProperties = { opacity: 0, transform: "translateY(12px)", filter: "blur(6px)" };
const SHOWN: React.CSSProperties = { opacity: 1, transform: "translateY(0)", filter: "blur(0)" };

export function RevealGroup({ children, className }: { children: ReactNode; className?: string }) {
  const reduced = usePrefersReducedMotion();
  const { ref, visible } = useInViewOnce<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={className}
      data-reveal-group
      data-visible={visible ? "true" : "false"}
      data-reduced={reduced ? "true" : "false"}
      style={reduced ? undefined : { ...BASE_STYLE, ...(visible ? SHOWN : HIDDEN) }}
    >
      {children}
    </div>
  );
}

export function RevealItem({
  children,
  className,
  index = 0,
  delay,
}: {
  children: ReactNode;
  className?: string;
  /** Position within a RevealGroup for stagger (0-based). */
  index?: number;
  /** Override explicit delay in ms. */
  delay?: number;
}) {
  const reduced = usePrefersReducedMotion();
  const { ref, visible } = useInViewOnce<HTMLDivElement>();
  const d = typeof delay === "number" ? delay : Math.min(index * 60, 360);
  const style: React.CSSProperties | undefined = reduced
    ? undefined
    : visible
      ? { ...BASE_STYLE, ...SHOWN, transitionDelay: `${d}ms` }
      : { ...BASE_STYLE, ...HIDDEN };
  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}
